// Google Ads OAuth2 Callback Page
// Location: /src/modules/googleAds/GoogleAdsCallback.tsx

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Loader2, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Sparkles,
  ArrowRight
} from 'lucide-react';
import { googleAuthService } from './services/googleAuthService';

const GoogleAdsCallback: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Przetwarzanie autoryzacji...');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    handleCallback();
  }, []);

  const handleCallback = async () => {
    try {
      // Get OAuth2 parameters
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const error = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      // Check for errors from Google
      if (error) {
        throw new Error(errorDescription || `Google OAuth error: ${error}`);
      }

      // Check if we have the authorization code
      if (!code) {
        throw new Error('Brak kodu autoryzacji od Google');
      }

      setMessage('Wymiana kodu na token dostępu...');

      // Exchange code for tokens
      const user = await googleAuthService.handleOAuth2Callback(code, state || '');

      if (user) {
        setStatus('success');
        setMessage(`Witaj, ${user.name}!`);
        
        // Redirect to Google Ads dashboard after success
        setTimeout(() => {
          navigate('/google-ads');
        }, 2000);
      } else {
        throw new Error('Nie udało się pobrać danych użytkownika');
      }
    } catch (err: any) {
      console.error('OAuth callback error:', err);
      setStatus('error');
      setError(err.message || 'Wystąpił błąd podczas autoryzacji');
      setMessage('Autoryzacja nie powiodła się');

      // Redirect to Google Ads with error after delay
      setTimeout(() => {
        navigate('/google-ads?auth=error');
      }, 3000);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6">
      {/* Animated background */}
      <div className="fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-purple-950/20 to-slate-950" />
        
        <div className="absolute top-0 -left-4 w-96 h-96 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob" />
        <div className="absolute top-0 -right-4 w-96 h-96 bg-pink-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-2000" />
        <div className="absolute -bottom-8 left-20 w-96 h-96 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-4000" />
      </div>

      {/* Content */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="relative w-full max-w-md"
      >
        <div className="rounded-3xl bg-white/5 backdrop-blur-2xl border border-white/10 p-8 shadow-2xl">
          {/* Status Icon */}
          <motion.div 
            className="flex justify-center mb-6"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring" }}
          >
            <div className="relative">
              {status === 'processing' && (
                <div className="p-4 rounded-full bg-blue-500/20">
                  <Loader2 className="w-12 h-12 text-blue-400 animate-spin" />
                </div>
              )}
              {status === 'success' && (
                <div className="p-4 rounded-full bg-green-500/20">
                  <CheckCircle className="w-12 h-12 text-green-400" />
                </div>
              )}
              {status === 'error' && (
                <div className="p-4 rounded-full bg-red-500/20">
                  <XCircle className="w-12 h-12 text-red-400" />
                </div>
              )}
              
              {/* Animated sparkles for success */}
              {status === 'success' && (
                <>
                  <Sparkles className="absolute -top-2 -right-2 w-6 h-6 text-yellow-400 animate-pulse" />
                  <Sparkles className="absolute -bottom-2 -left-2 w-5 h-5 text-purple-400 animate-pulse animation-delay-500" />
                </>
              )}
            </div>
          </motion.div>

          {/* Title */}
          <motion.h2 
            className={`text-2xl font-bold text-center mb-2 ${
              status === 'success' ? 'text-green-400' : 
              status === 'error' ? 'text-red-400' : 
              'text-white'
            }`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            {status === 'processing' && 'Autoryzacja Google Ads'}
            {status === 'success' && 'Autoryzacja zakończona!'}
            {status === 'error' && 'Błąd autoryzacji'}
          </motion.h2>

          {/* Message */}
          <motion.p 
            className="text-center text-white/60 mb-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            {message}
          </motion.p>

          {/* Error details */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/20"
            >
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-300">Szczegóły błędu:</p>
                  <p className="text-xs text-red-300/60 mt-1">{error}</p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Progress bar */}
          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
            <motion.div
              className={`h-full ${
                status === 'success' ? 'bg-green-500' : 
                status === 'error' ? 'bg-red-500' : 
                'bg-blue-500'
              }`}
              initial={{ width: '0%' }}
              animate={{ 
                width: status === 'processing' ? '60%' : '100%' 
              }}
              transition={{ duration: 1 }}
            />
          </div>

          {/* Actions */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="mt-6 text-center"
          >
            {status === 'processing' && (
              <p className="text-xs text-white/40">
                Proszę czekać, nie zamykaj tej strony...
              </p>
            )}
            
            {status === 'success' && (
              <p className="text-sm text-green-400/80 flex items-center justify-center gap-2">
                Przekierowanie za chwilę
                <ArrowRight className="w-4 h-4 animate-pulse" />
              </p>
            )}
            
            {status === 'error' && (
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/google-ads')}
                  className="w-full px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 transition-colors text-sm font-medium text-white/90"
                >
                  Wróć do Google Ads
                </button>
                <button
                  onClick={() => window.location.reload()}
                  className="w-full px-4 py-2 rounded-xl bg-blue-500/20 hover:bg-blue-500/30 transition-colors text-sm font-medium text-blue-300"
                >
                  Spróbuj ponownie
                </button>
              </div>
            )}
          </motion.div>
        </div>

        {/* Footer info */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="text-center text-xs text-white/30 mt-4"
        >
          PlexiSystem × Google Ads API v2.0
        </motion.p>
      </motion.div>

      {/* Custom styles */}
      <style jsx>{`
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        
        .animate-blob {
          animation: blob 7s infinite;
        }
        
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        
        .animation-delay-4000 {
          animation-delay: 4s;
        }
        
        .animation-delay-500 {
          animation-delay: 0.5s;
        }
      `}</style>
    </div>
  );
};

export default GoogleAdsCallback;
